export type SurroundStyle =
  | "dim"
  | "dim-light"
  | "dim-heavy"
  | "white-wash"
  | "white-wash-light"
  | "white-wash-heavy"
  | "gray-wash"
  | "grayscale"
  | "grayscale-light"
  | "grayscale-dim"
  | "blur-xs"
  | "blur"
  | "blur-md"
  | "blur-strong"
  | "blur-xl"
  | "blur-xxl"
  | "blur-dim"
  | "blur-grayscale-xs"
  | "grayscale-blur"
  | "grayscale-blur-strong"
  | "grayscale-blur-dim"
  | "white-wash-blur-xs"
  | "white-wash-blur"
  | "white-wash-blur-strong"
  | "white-wash-light-blur"
  | "white-wash-heavy-blur"
  | "white-wash-grayscale"
  | "white-wash-grayscale-light"
  | "white-wash-grayscale-blur"
  | "white-wash-grayscale-blur-strong"
  | "white-wash-light-grayscale-blur"
  | "vignette"
  | "sepia"
  | "none";

export interface SurroundParams {
  grayscale: number; // 0–1
  blurPx: number; // target display-px blur
  darken: number; // 0–1 black overlay opacity
  lighten: number; // 0–1 white overlay opacity
  grayWash: number; // 0–1 neutral gray overlay opacity
  sepia: number; // 0–1
  vignette: boolean;
}

const empty: SurroundParams = {
  grayscale: 0,
  blurPx: 0,
  darken: 0,
  lighten: 0,
  grayWash: 0,
  sepia: 0,
  vignette: false,
};

export function resolveSurround(style: SurroundStyle): SurroundParams {
  switch (style) {
    case "none":
      return empty;
    case "dim":
      return { ...empty, darken: 0.5 };
    case "dim-light":
      return { ...empty, darken: 0.3 };
    case "dim-heavy":
      return { ...empty, darken: 0.65 };

    case "white-wash-light":
      return { ...empty, lighten: 0.35 };
    case "white-wash":
      return { ...empty, lighten: 0.55 };
    case "white-wash-heavy":
      return { ...empty, lighten: 0.75 };
    case "gray-wash":
      return { ...empty, grayWash: 0.55 };

    case "grayscale-light":
      return { ...empty, grayscale: 0.6 };
    case "grayscale":
      return { ...empty, grayscale: 1 };
    case "grayscale-dim":
      return { ...empty, grayscale: 1, darken: 0.3 };

    case "blur-xs":
      return { ...empty, blurPx: 3 };
    case "blur":
      return { ...empty, blurPx: 6 };
    case "blur-md":
      return { ...empty, blurPx: 10 };
    case "blur-strong":
      return { ...empty, blurPx: 14 };
    case "blur-xl":
      return { ...empty, blurPx: 20 };
    case "blur-xxl":
      return { ...empty, blurPx: 30 };
    case "blur-dim":
      return { ...empty, blurPx: 6, darken: 0.3 };
    case "blur-grayscale-xs":
      return { ...empty, blurPx: 3, grayscale: 1 };

    case "grayscale-blur":
      return { ...empty, grayscale: 1, blurPx: 5 };
    case "grayscale-blur-strong":
      return { ...empty, grayscale: 1, blurPx: 14 };
    case "grayscale-blur-dim":
      return { ...empty, grayscale: 1, blurPx: 5, darken: 0.25 };

    case "white-wash-blur-xs":
      return { ...empty, lighten: 0.45, blurPx: 3 };
    case "white-wash-blur":
      return { ...empty, lighten: 0.45, blurPx: 6 };
    case "white-wash-blur-strong":
      return { ...empty, lighten: 0.45, blurPx: 14 };
    case "white-wash-light-blur":
      return { ...empty, lighten: 0.3, blurPx: 6 };
    case "white-wash-heavy-blur":
      return { ...empty, lighten: 0.7, blurPx: 6 };

    case "white-wash-grayscale":
      return { ...empty, lighten: 0.4, grayscale: 1 };
    case "white-wash-grayscale-light":
      return { ...empty, lighten: 0.3, grayscale: 0.7 };
    case "white-wash-grayscale-blur":
      return { ...empty, lighten: 0.4, grayscale: 1, blurPx: 5 };
    case "white-wash-grayscale-blur-strong":
      return { ...empty, lighten: 0.4, grayscale: 1, blurPx: 14 };
    case "white-wash-light-grayscale-blur":
      return { ...empty, lighten: 0.3, grayscale: 0.8, blurPx: 4 };

    case "sepia":
      return { ...empty, sepia: 0.8 };
    case "vignette":
      return { ...empty, vignette: true };
  }
}
